// src/pages/packingBatch/DispatchSingleOrderPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Scan,
  CheckCircle,
  AlertCircle,
  Loader2,
  Calendar,
  Truck,
  User,
  Phone,
  Clock,
  TrendingUp,
  X,
  BarChart3,
  Package,
  Hash
} from 'lucide-react';
import { dispatchSingleOrder, getDispatchStatistics } from '../../services/api/dispatch';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatTime } from '../../utils/dateUtils';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import styles from './DispatchSingleOrderPage.module.css';

const DispatchSingleOrderPage = () => {
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success: showSuccess, error: showError } = useAlert();
  const inputRef = useRef(null);

  // Form state
  const [identifier, setIdentifier] = useState('');
  const [dispatchedDate, setDispatchedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Statistics
  const [todayStats, setTodayStats] = useState({ totalDispatched: 0, totalValue: 0 });
  const [sessionCount, setSessionCount] = useState(0);

  // Recent dispatches (session only)
  const [recentDispatches, setRecentDispatches] = useState([]);

  // Current dispatch result
  const [currentDispatch, setCurrentDispatch] = useState(null);
  const [error, setError] = useState(null);

  // Permissions
  const canBackdateDispatch = hasPermission('backdated_dispatch');

  // Audio feedback
  const successAudioRef = useRef(new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBCl+zPHaizsIHGS57OihUQ0PVK3n77BdGAg+mdv0xnMoBSuAzvLZjToIGme78OScTgwOUqji8bllHAU2jdXzzn0vBSh7yvDekkAJElyx6OyrWBYKQ5zd8sFuJAQnfMrw3Y8+CRRas+jvo1QQDFWt5vCwYhoIOZXX8sh0KwUrgs7y2Ys5CBlluu7mnlEODlOq5PC0aB4GLIvP8MZ2LgUrf87w3I4+CRZftulwpFgTDEmh3+29bC')); 
  const errorAudioRef = useRef(new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBgoOFh4mJiomJhIOAfXt5d3VycW9ubGppaGdmZWVkZGNjY2JiYmJiYmJiYmJiYmNjY2RkZWZnaGlrbG5wcnV3eXt9gIOFh4mKiomJh4WDgX97d3NwbGhlYV5aV1RSUE5MSklHRkRDQkFAQD8/Pz4+Pj4+Pj4+Pj4+Pz9AQEFCREVHSElLTU9RVFdaXmFlaG1wcXZ5fX+Dh4mLjY+QkZKTk5SUlJSUlJOTkpGQj42MioiGhIOBfnx5dnRxb2xpZmNgXVpXVVJQTkxKSEZFQ0JBQEBAQEBAQEBBQkNFRkhKTE5QUlVXWl1gY2ZpbHF0d3p8f4GDhoiKjI6QkpOUlZWWlpaWlpWVlJOSkZCOjYuJh4WDgX99enZ0cW9saWZjYF1aV1RSUE5NSUtJSEdGRURDQ0JCQkJCQkJCQ0NFRkhKTE5RVFdaXWFkaGtwdHd7foGFiIuNj5KUlZaYmJmZmZmZmJiXlpWUkpCOjIqHhYOAfl1ZVlNQTUqF'));

  // Load today's statistics
  useEffect(() => {
    loadTodayStats();
  }, []);

  // Auto-focus input on mount and after dispatch
  useEffect(() => {
    if (inputRef.current && !isProcessing) {
      inputRef.current.focus();
    }
  }, [isProcessing, currentDispatch, error]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        handleClear();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadTodayStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const stats = await getDispatchStatistics({
        dateFrom: today,
        dateTo: today
      });
      setTodayStats({
        totalDispatched: stats.totalDispatched || 0,
        totalValue: stats.totalValue || 0
      });
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!identifier.trim()) {
      setError(t('dispatch.enterIdentifier'));
      return;
    }

    setIsProcessing(true);
    setError(null);
    setCurrentDispatch(null);

    try {
      const result = await dispatchSingleOrder({
        identifier: identifier.trim(),
        dispatchedDate: dispatchedDate
      });

      successAudioRef.current?.play().catch(() => {});

      setCurrentDispatch(result);

      const dispatchRecord = {
        ...result.order,
        courier: result.courier,
        warehouse: result.warehouse,
        dispatchedAt: new Date().toISOString()
      };
      setRecentDispatches(prev => [dispatchRecord, ...prev.slice(0, 9)]);

      setSessionCount(prev => prev + 1);
      setTodayStats(prev => ({
        totalDispatched: prev.totalDispatched + 1,
        totalValue: prev.totalValue + parseFloat(result.order.grandTotal || 0)
      }));

      setIdentifier('');

      showSuccess(t('dispatch.dispatchSuccess', { orderNumber: result.order.orderNumber }));

    } catch (error) {
      console.error('Dispatch error:', error);
      
      errorAudioRef.current?.play().catch(() => {});

      const errorMessage = error.response?.data?.message || error.message || t('dispatch.dispatchFailed');
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClear = () => {
    setIdentifier('');
    setError(null);
    setCurrentDispatch(null);
    inputRef.current?.focus();
  };

  const isBackdated = dispatchedDate < new Date().toISOString().split('T')[0];

  return (
    <div className={styles.dispatchPage}>
      <PageHeader
        title={t('dispatch.pageTitle')}
        description={t('dispatch.pageDescription')}
        icon={Scan}
      />

      {/* Statistics Dashboard */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#dbeafe' }}>
            <Package size={24} style={{ color: '#2563eb' }} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>{t('dispatch.todayDispatches')}</div>
            <div className={styles.statValue}>{todayStats.totalDispatched}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#dcfce7' }}>
            <TrendingUp size={24} style={{ color: '#16a34a' }} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>{t('dispatch.todayValue')}</div>
            <div className={styles.statValue}>{formatCurrency(todayStats.totalValue)}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#fef3c7' }}>
            <BarChart3 size={24} style={{ color: '#d97706' }} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>{t('dispatch.thisSession')}</div>
            <div className={styles.statValue}>{sessionCount}</div>
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        {/* Scan Section */}
        <div className={styles.scanSection}>
          <div className={styles.scanCard}>
            <div className={styles.scanHeader}>
              <Scan size={32} />
              <h2>{t('dispatch.scanToDispatch')}</h2>
            </div>

            <form onSubmit={handleSubmit} className={styles.scanForm}>
              {/* Main Input */}
              <div className={styles.inputGroup}>
                <label>{t('dispatch.orderIdentifier')} *</label>
                <div className={styles.scanInputWrapper}>
                  <Hash className={styles.inputIcon} size={20} />
                  <input
                    ref={inputRef}
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder={t('dispatch.identifierPlaceholder')}
                    className={styles.scanInput}
                    disabled={isProcessing}
                    autoComplete="off"
                  />
                  {identifier && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className={styles.clearButton}
                      disabled={isProcessing}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              </div>

              {/* Date Picker */}
              <div className={styles.inputGroup}>
                <label>
                  {t('dispatch.dispatchDate')}
                  {isBackdated && !canBackdateDispatch && (
                    <span className={styles.warningLabel}>
                      <AlertCircle size={14} />
                      {t('dispatch.pastDatesNotAllowed')}
                    </span>
                  )}
                </label>
                <div className={styles.dateInputWrapper}>
                  <Calendar className={styles.inputIcon} size={18} />
                  <input
                    type="date"
                    value={dispatchedDate}
                    onChange={(e) => setDispatchedDate(e.target.value)}
                    max={canBackdateDispatch ? undefined : new Date().toISOString().split('T')[0]}
                    className={styles.dateInput}
                    disabled={isProcessing}
                  />
                </div>
                {isBackdated && (
                  <div className={styles.backdateWarning}>
                    <AlertCircle size={14} />
                    <span>{t('dispatch.backdatedDispatch')}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className={styles.actionButtons}>
                <button
                  type="submit"
                  disabled={isProcessing || !identifier.trim()}
                  className={styles.dispatchButton}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 size={20} className={styles.spinning} />
                      {t('dispatch.processing')}
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      {t('dispatch.dispatchOrder')}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={isProcessing}
                  className={styles.clearButtonLarge}
                >
                  <X size={20} />
                  {t('common.clear')}
                </button>
              </div>
            </form>

            {/* Error Display */}
            {error && (
              <div className={styles.errorAlert}>
                <AlertCircle size={20} />
                <div className={styles.errorContent}>
                  <strong>{t('dispatch.dispatchFailed')}</strong>
                  <p>{error}</p>
                </div>
              </div>
            )}

            {/* Success Display */}
            {currentDispatch && (
              <div className={styles.successAlert}>
                <div className={styles.successHeader}>
                  <CheckCircle size={24} />
                  <h3>{t('dispatch.successfullyDispatched')}</h3>
                </div>
                
                <div className={styles.dispatchDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('dispatch.orderNumber')}:</span>
                    <span className={styles.detailValue}>#{currentDispatch.order.orderNumber}</span>
                  </div>
                  
                  {currentDispatch.order.airwaybillNumber && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('dispatch.airwaybill')}:</span>
                      <span className={styles.detailValue}>{currentDispatch.order.airwaybillNumber}</span>
                    </div>
                  )}

                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('dispatch.customer')}:</span>
                    <span className={styles.detailValue}>{currentDispatch.order.customerName}</span>
                  </div>

                  <div className={styles.courierBadge} style={{
                    backgroundColor: `${currentDispatch.courier.colorCode}15`,
                    borderColor: currentDispatch.courier.colorCode
                  }}>
                    <Truck size={16} style={{ color: currentDispatch.courier.colorCode }} />
                    <span style={{ color: currentDispatch.courier.colorCode }}>
                      {currentDispatch.courier.accountName}
                    </span>
                  </div>

                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('dispatch.status')}:</span>
                    <span className={styles.statusBadge}>{currentDispatch.order.newStatus}</span>
                  </div>

                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('dispatch.totalAmount')}:</span>
                    <span className={styles.amountValue}>
                      {formatCurrency(currentDispatch.order.grandTotal)}
                    </span>
                  </div>

                  {currentDispatch.order.codAmount > 0 && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('dispatch.codAmount')}:</span>
                      <span className={styles.codValue}>
                        {formatCurrency(currentDispatch.order.codAmount)}
                      </span>
                    </div>
                  )}

                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('dispatch.location')}:</span>
                    <span className={styles.detailValue}>
                      {[
                        currentDispatch.order.city,
                        currentDispatch.order.district,
                        currentDispatch.order.province
                      ].filter(Boolean).join(', ')}
                    </span>
                  </div>

                  {currentDispatch.order.phoneNumbers?.length > 0 && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>{t('dispatch.phone')}:</span>
                      <span className={styles.detailValue}>
                        {currentDispatch.order.phoneNumbers.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Keyboard Shortcuts */}
          <div className={styles.shortcutsCard}>
            <h3>{t('dispatch.keyboardShortcuts')}</h3>
            <div className={styles.shortcutsList}>
              <div className={styles.shortcutItem}>
                <kbd>F2</kbd>
                <span>{t('dispatch.focusInput')}</span>
              </div>
              <div className={styles.shortcutItem}>
                <kbd>Enter</kbd>
                <span>{t('dispatch.submitScan')}</span>
              </div>
              <div className={styles.shortcutItem}>
                <kbd>ESC</kbd>
                <span>{t('dispatch.clearForm')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Dispatches */}
        <div className={styles.recentSection}>
          <div className={styles.recentCard}>
            <div className={styles.recentHeader}>
              <h2>
                <Clock size={20} />
                {t('dispatch.recentDispatches')}
              </h2>
              <span className={styles.recentCount}>{recentDispatches.length}</span>
            </div>

            {recentDispatches.length > 0 ? (
              <div className={styles.recentList}>
                {recentDispatches.map((dispatch, index) => (
                  <div key={index} className={styles.recentItem}>
                    <div className={styles.recentIcon}>
                      <CheckCircle size={20} style={{ color: '#16a34a' }} />
                    </div>
                    <div className={styles.recentContent}>
                      <div className={styles.recentOrder}>
                        <strong>#{dispatch.orderNumber}</strong>
                        {dispatch.airwaybillNumber && (
                          <span className={styles.recentAwb}>{dispatch.airwaybillNumber}</span>
                        )}
                      </div>
                      <div className={styles.recentCustomer}>
                        <User size={12} />
                        {dispatch.customerName}
                      </div>
                      {dispatch.courier && (
                        <div className={styles.recentCourier} style={{
                          color: dispatch.courier.colorCode
                        }}>
                          <Truck size={12} />
                          {dispatch.courier.accountName}
                        </div>
                      )}
                    </div>
                    <div className={styles.recentTime}>
                      {formatTime(dispatch.dispatchedAt)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyRecent}>
                <Package size={48} />
                <p>{t('dispatch.noDispatchesYet')}</p>
                <span>{t('dispatch.dispatchesWillAppear')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DispatchSingleOrderPage;